/**
 * Game Constants
 *
 * All game constants externalized for easy tuning.
 * These values will be adjusted during playtesting.
 */

export const GAME_CONSTANTS = {
  // Jump mechanics (defaults - upgradeable via collectables)
  JUMP: {
    MIN_CHARGE_TIME: 0.05, // seconds (50ms) - instant tap for tiny hop
    MAX_CHARGE_TIME: 1.0, // seconds (1000ms) - reduced from 1.5s
    MIN_VELOCITY: -4, // Minimum jump velocity (tiny hop ~40px)
    MAX_VELOCITY: -14, // Maximum jump velocity (reduced by 50% from -28)
  },

  // Blobo (player) properties
  BLOBO: {
    RADIUS: 15, // 50% smaller for better platform navigation (was 30)
    MASS: 1,
    FRICTION: 0.3, // Lower friction so platforms have more control (was 0.8)
    RESTITUTION: 0.1, // Low restitution so platforms control bounce
    DENSITY: 0.002, // Higher density for marble-like weight
    AIR_RESISTANCE: 0.002, // More air resistance (50% less momentum)
    INERTIA: Infinity, // Prevent rotation resistance - rolls freely
    SQUASH_MIN_SCALE_Y: 0.5, // Minimum Y scale when fully charged (0.5 = 50% height)
    SQUASH_MAX_SCALE_X: 1.3, // Maximum X scale to maintain volume
    STRETCH_SCALE_Y: 1.5, // Jump stretch Y scale
    STRETCH_SCALE_X: 0.7, // Jump stretch X scale (narrower)
    STRETCH_DURATION: 200, // ms
    HEIGHT_SCALE: 90, // Phase 5: 1 meter = Blobo diameter (30px) * 3 = 90 pixels
  },

  // Physics world settings
  PHYSICS: {
    GRAVITY: 1.2, // World gravity
    FALL_THRESHOLD: 500, // Distance to trigger big fall animation (pixels)
    TERMINAL_VELOCITY: 2000, // Max fall speed (pixels/sec)
  },

  // Camera settings
  CAMERA: {
    LERP_X: 0.1, // Horizontal follow smoothing
    LERP_Y: 0.15, // Vertical follow smoothing
    ROTATION_SMOOTHING: 0.2, // World rotation smoothing
    ZOOM: 1.0, // Default zoom level
  },

  // Tilt controls
  TILT: {
    MAX_ANGLE: 15, // Max tilt angle in degrees (±15°) - side edge tilt range
    DEADZONE: 1, // Ignore tilts smaller than this (degrees)
    SMOOTHING: 0.15, // Tilt smoothing factor (lower = more responsive)
  },

  // Level generation
  LEVEL: {
    SECTION_HEIGHT: 1500, // Height of each level section (pixels)
    PLATFORM_CLEANUP_DISTANCE: 2000, // Distance below camera to cleanup platforms
    MIN_PLATFORM_WIDTH: 80, // Minimum platform width
    MAX_PLATFORM_WIDTH: 200, // Maximum platform width
    TOTAL_HEIGHT: 10000, // Total height to reach summit
  },

  // Surface types friction/restitution values
  // DRAMATICALLY INCREASED for Phase 3 - each surface should feel VERY different
  // Note: Matter.js calculates combined friction as: sqrt(frictionA * frictionB)
  // And restitution as: max(restitutionA, restitutionB)
  SURFACE: {
    NORMAL: {
      FRICTION: 0.5, // Moderate friction
      RESTITUTION: 0.1, // Low bounce
      COLOR: 0x808080, // Gray
    },
    STICKY: {
      FRICTION: 10.0, // EXTREME friction - you stop IMMEDIATELY
      RESTITUTION: 0.0, // No bounce at all
      COLOR: 0x00ff00, // Green
      JUMP_MULTIPLIER: 0.5, // Reduce jump height to 50% when launching from sticky
    },
    SLIPPERY: {
      FRICTION: 0.0, // Zero friction - maximum slidiness
      RESTITUTION: 0.05, // Tiny bounce
      COLOR: 0x00aaff, // Blue
    },
    BOUNCY: {
      FRICTION: 0.2, // Low friction
      RESTITUTION: 1.2, // Bounce boost (was 1.0)
      COLOR: 0xff4400, // Orange/Red
    },
    ANGLED: {
      FRICTION: 0.1, // Low friction for sliding (was 0.4)
      RESTITUTION: 0.1, // Low bounce
      COLOR: 0xffff00, // Yellow
    },
  },

  // UI settings
  UI: {
    FONT_FAMILY: '"Josefin Sans", Arial, sans-serif',
    FONT_SIZE_LARGE: 32,
    FONT_SIZE_MEDIUM: 24,
    FONT_SIZE_SMALL: 16,
    COLOR_PRIMARY: '#ffffff',
    COLOR_SECONDARY: '#cccccc',
    COLOR_ACCENT: '#ffaa00',
  },

  // Performance settings
  PERFORMANCE: {
    MAX_PARTICLES: 50,
    OBJECT_POOL_SIZE: 100,
    TARGET_FPS: 60,
  },

  // Debug settings (dev only)
  DEBUG: {
    SHOW_PHYSICS: false, // Disabled - causes severe performance issues
    SHOW_FPS: false, // Disabled - updates 60fps causing lag
    SHOW_TILT_ANGLE: false,
    KEYBOARD_TILT: true, // Allow keyboard simulation of tilt

    // iOS Performance Optimizations (v0.3.4 - PRODUCTION)
    DISABLE_DECORATIONS: false, // v0.3.1: NOT the bottleneck
    DISABLE_BACKGROUNDS: false, // v0.3.4: Optimized 51→18 layers, re-enabled
    DISABLE_AUDIO: false, // Not an issue
    DISABLE_ANIMATED_PLATFORMS: false, // v0.3.3: NOT the bottleneck
  },

  // Victory Zone (Phase 5)
  VICTORY: {
    PODIUM_Y: 11650, // 225px above platform #104 (11425) - just reachable with perfect max jump (~200-250px) - in LEVEL coordinates
    PODIUM_BASE_WIDTH: 300, // Base platform width
    PODIUM_X: 400, // Center of screen
    STEP_WIDTHS: [150, 100, 50], // Bottom to top
    STEP_HEIGHTS: [30, 30, 30], // Each step height
    TRIGGER_HEIGHT: 20, // Victory trigger zone height

    MEDAL_THRESHOLDS: {
      GOLD: 300000, // 5 minutes in ms
      SILVER: 480000, // 8 minutes in ms
      BRONZE: 900000, // 15 minutes in ms
    },

    CELEBRATION_DURATION: 5000, // 5 seconds of effects
    FIREWORK_BURSTS: 5, // Number of firework bursts
    CONFETTI_DURATION: 3000, // Confetti fall duration
  },
} as const;

// Surface type enum
export enum SurfaceType {
  NORMAL = 'NORMAL',
  STICKY = 'STICKY',
  SLIPPERY = 'SLIPPERY',
  BOUNCY = 'BOUNCY',
  ANGLED = 'ANGLED',
}

// Game events
export const GAME_EVENTS = {
  BLOBO_JUMP: 'blobo:jump',
  BLOBO_LAND: 'blobo:land',
  BLOBO_FALL: 'blobo:fall',
  BLOBO_HEIGHT_CHANGE: 'blobo:height:change',
  HEIGHT_RECORD: 'height:record',
  GAME_PAUSE: 'game:pause',
  GAME_RESUME: 'game:resume',
  UPGRADE_COLLECT: 'upgrade:collect',
  CHECKPOINT_REACH: 'checkpoint:reach',
  TIMER_START: 'timer:start',
  VICTORY_ACHIEVED: 'victory:achieved',
  STATS_UPDATE: 'stats:update',
} as const;
